import {
	Button,
	Card,
	DropdownMenu,
	Flex,
	Heading,
	Link as RLink,
} from '@radix-ui/themes'
import {
	Link,
	useLocation,
	useNavigate,
	useNavigation,
	useOutletContext,
	useSearchParams,
} from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { type GetTaxon } from '#app/models/ecommerce-providers/sylius/shop/fetch/taxon/get-taxon.ts'
import { type GetTaxons } from '#app/models/ecommerce-providers/sylius/shop/fetch/taxon/get-taxons.ts'
import {
	PRODUCTS_INDEX_I18NEXT,
	PRODUCTS_INDEX_SEARCH_PARAMS,
} from '#app/routes/($lang).products._index/route-config'
import { useProductsTaxonNotify } from '#app/routes/($lang).products._index/use-products-taxon-notify.tsx'
import { type Context } from '#app/routes/_layout/route-config.ts'
import { SEARCH_SEARCH_PARAMS } from '#app/routes/_layout/search'
import { testId } from '#app/utils/client/test-id.ts'

/* -------------------------------------------------------------------------------------------------
 * TEST_IDS
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	filterByTaxon = 'filter-by-taxon',
	filterByTaxonSelect = 'filter-by-taxon-select',
}

/* -------------------------------------------------------------------------------------------------
 * DATA ATTRIBUTES
 * -----------------------------------------------------------------------------------------------*/

enum DATA_ATTRIBUTES {
	testId = 'data-testid',
	code = 'data-code',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SELECT_TAXON_SEARCH_PARAMS {
	taxon = 'taxon',
}

/* -------------------------------------------------------------------------------------------------
 * TaxonFilter
 * -----------------------------------------------------------------------------------------------*/

type TaxonFilterProps = {
	currentTaxon: GetTaxon | undefined
	taxons: GetTaxons | undefined
}

const TaxonFilter: React.FC<TaxonFilterProps> = ({ currentTaxon, taxons }) => {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [searchParams] = useSearchParams()
	const { setApp } = useOutletContext<Context>()
	const navigation = useNavigation()
	const { t } = useTranslation(PRODUCTS_INDEX_I18NEXT.routeProductsIndex)

	const locationTaxon = new URLSearchParams(navigation.location?.search).get(
		SELECT_TAXON_SEARCH_PARAMS.taxon,
	)
	const optimisticTaxon = locationTaxon
		? taxons?.items?.find(({ code }: GetTaxon) => code === locationTaxon) ||
			undefined
		: currentTaxon

	useProductsTaxonNotify({ setApp, t, optimisticTaxon, navigation })

	return (
		<>
			<nav className="app-SidePanel sticky hidden p-3 py-4 lg:block">
				<Card size="3">
					{!!taxons?.items && (
						<Flex direction="column" gapY="3">
							<Heading as="h3" size="6" color="green">
								{t('label.taxons')}
							</Heading>
							<Flex
								asChild
								direction="column"
								gapY="2"
								ml="1"
								{...testId(TEST_IDS.filterByTaxon)}
							>
								<ul>
									{taxons.items.map(({ code, name }: GetTaxon) => (
										<li
											key={code}
											{...{
												[DATA_ATTRIBUTES.code]: code,
											}}
										>
											<Link
												aria-selected={code !== optimisticTaxon?.code}
												className="whitespace-nowrap py-1"
												prefetch="viewport"
												to={(() => {
													searchParams.delete(SEARCH_SEARCH_PARAMS.q)
													searchParams.delete(PRODUCTS_INDEX_SEARCH_PARAMS.page)
													searchParams.delete(
														PRODUCTS_INDEX_SEARCH_PARAMS.itemsPerPage,
													)
													code === optimisticTaxon?.code
														? searchParams.delete(
																SELECT_TAXON_SEARCH_PARAMS.taxon,
															)
														: searchParams.set(
																SELECT_TAXON_SEARCH_PARAMS.taxon,
																code!,
															)
													searchParams.sort()
													return `${pathname}?${searchParams}`
												})()}
											>
												<RLink
													asChild
													size="3"
													weight={
														code === optimisticTaxon?.code ? 'bold' : 'regular'
													}
													color={
														code === optimisticTaxon?.code ? 'green' : 'gray'
													}
												>
													<span>{name}</span>
												</RLink>
											</Link>
										</li>
									))}
								</ul>
							</Flex>
						</Flex>
					)}
				</Card>
			</nav>
			{!!taxons?.items && (
				<Flex align="center" gapX="2" px="3" display={{ lg: 'none' }}>
					<Heading as="h3" size="4" color="gray">
						{t('label.taxons')}
					</Heading>
					<DropdownMenu.Root>
						<DropdownMenu.Trigger>
							<Button variant="soft">
								{t(optimisticTaxon?.name || 'button.selectATaxon')}
								<DropdownMenu.TriggerIcon />
							</Button>
						</DropdownMenu.Trigger>
						<DropdownMenu.Content>
							{!!optimisticTaxon && (
								<>
									<DropdownMenu.Item onClick={() => navigate({ pathname })}>
										{t('items.resetFilter')}
									</DropdownMenu.Item>
									<DropdownMenu.Separator />
								</>
							)}
							{taxons?.items?.map(({ code, name }: GetTaxon) => (
								<DropdownMenu.Item
									key={code}
									disabled={optimisticTaxon?.code === code}
									onSelect={() => {
										navigate({
											pathname,
											search: `?${SELECT_TAXON_SEARCH_PARAMS.taxon}=${code}`,
										})
									}}
								>
									{name}
								</DropdownMenu.Item>
							))}
						</DropdownMenu.Content>
					</DropdownMenu.Root>
				</Flex>
			)}
		</>
	)
}
TaxonFilter.displayName = 'TaxonFilter'

/* -----------------------------------------------------------------------------------------------*/

export {
	TaxonFilter,
	TEST_IDS as PRODUCTS_INDEX_SELECT_TAXON_TEST_IDS,
	DATA_ATTRIBUTES as PRODUCTS_INDEX_SELECT_TAXON_DATA_ATTRIBUTES,
	SELECT_TAXON_SEARCH_PARAMS as PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS,
}
